/**
 * ENG-6195: shared contract for the admin debug surface — the diagnostic
 * projection types, the explicit DB column allow-lists, and the scope value
 * that authorises a read.
 *
 * Design council (ENG-6195) hard requirements baked in here:
 *
 *  1. **Diagnostic projection, never raw rows.** The debug surface returns
 *     verdicts / enums / safe metadata, NEVER credentials, tokens, transcripts,
 *     `agent_memories`, settings blobs, or PII. The column allow-lists below are
 *     the structural gate: the API selects exactly these columns (never
 *     `select('*')`), so a newly-added sensitive column on `agents` /
 *     `agent_integrations` is invisible to a cross-org reader until someone
 *     deliberately adds it here — the safe default for a cross-org egress tool.
 *
 *  2. **Scope is a value, not a hard-coded branch.** `DebugScope` is the
 *     parameter the whole surface pivots on. Staff resolve to `{ kind:
 *     'all-orgs' }`; the future org-admin case is a new branch returning
 *     `{ kind: 'single-org', orgId }` — every read takes a `DebugScope`, so the
 *     extension is a parameter, not a fork (ENG-6195 Architect).
 *
 * This module is pure types + constants + pure verdict helpers — no DB client,
 * no node:crypto — so it is safe to import from both the API and the
 * `@integrity-labs/augmented-admin-mcp` package.
 */

// ───────────────────────────── scope ─────────────────────────────

/**
 * The read-scope a debug caller is authorised for. Returned by the API's
 * `resolveDebugScope(caller, requestContext)`.
 *
 * - `all-orgs`   — Integrity Labs staff (owning org `is_internal = true`). No
 *                  organization filter is applied; the widening is a single
 *                  auditable line in the API.
 * - `single-org` — reserved for the future org-admin extension (ENG-6197 /
 *                  ENG-6198): the caller may read only `orgId`. Not produced by
 *                  any Slice-1 resolver branch yet, but every read already
 *                  honours it so adding the branch is wiring, not a rewrite.
 */
export type DebugScope =
  | { kind: 'all-orgs' }
  | { kind: 'single-org'; orgId: string };

/** True when `scope` permits reading rows belonging to `organizationId`. */
export function scopeAllowsOrg(scope: DebugScope, organizationId: string | null): boolean {
  if (scope.kind === 'all-orgs') return true;
  return organizationId != null && organizationId === scope.orgId;
}

// ───────────────────────── liveness verdicts ─────────────────────────

export type LivenessVerdict = 'fresh' | 'stale' | 'down' | 'unknown';

/**
 * Collapse a heartbeat/last-seen age into a coarse verdict. Pure, so the
 * projection never has to ship a raw timestamp a caller could correlate — the
 * enum is the diagnostic signal.
 *
 * Thresholds are deliberately generous (a managed agent heartbeats well inside
 * 2 min; 10 min without one is "down"). `null` age → `unknown` (never seen).
 */
export function livenessVerdict(ageSeconds: number | null): LivenessVerdict {
  if (ageSeconds == null) return 'unknown';
  if (ageSeconds < 0) return 'unknown';
  if (ageSeconds <= 120) return 'fresh';
  if (ageSeconds <= 600) return 'stale';
  return 'down';
}

/** Seconds between `iso` and `now` (default Date.now), or null if `iso` is null/invalid. */
export function ageSeconds(iso: string | null | undefined, nowMs: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((nowMs - t) / 1000);
}

// ───────────────────────── column allow-lists ─────────────────────────
//
// The EXACT base-table columns each read may select. Sensitive columns are
// deliberately absent: agents has no secret columns but we still enumerate;
// hosts omits `anthropic_api_key_fingerprint` and api-key internals;
// agent_integrations omits `credentials` / `config`; alerts omits nothing
// secret (payload can carry context, so it is NOT selected). NEVER `select('*')`.

export const AGENT_DEBUG_COLUMNS = [
  'agent_id',
  'team_id',
  'code_name',
  'display_name',
  'status',
  'environment',
  'risk_tier',
  'created_at',
  'updated_at',
  'last_heartbeat_at',
] as const;

export const HOST_DEBUG_COLUMNS = [
  'id',
  'name',
  'organization_id',
  'status',
  'framework',
  'framework_version',
  'last_seen_at',
  'ec2_instance_id',
  'ec2_region',
  'ec2_provisioning_status',
  'claude_auth_mode',
  'claude_auth_status',
  'claude_auth_expires_at',
] as const;

export const INTEGRATION_DEBUG_COLUMNS = [
  'id',
  'agent_id',
  'team_id',
  'definition_id',
  'status',
  'status_message',
  'auth_type',
  'last_connectivity_check_at',
  'last_connectivity_status',
  'consecutive_connectivity_failures',
  'updated_at',
] as const;

export const ALERT_DEBUG_COLUMNS = [
  'id',
  'kind',
  'severity',
  'message',
  'team_id',
  'host_id',
  'agent_id',
  'source',
  'opened_at',
  'closed_at',
  'closed_reason',
  'acknowledged_at',
  'snoozed_until',
] as const;

// ───────────────────────── projection DTOs ─────────────────────────

export interface AgentDebugProjection {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  status: string | null;
  environment: string | null;
  risk_tier: string | null;
  team_id: string | null;
  organization_id: string | null;
  organization_slug: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Coarse verdict from `last_heartbeat_at`; raw timestamp intentionally not shipped. */
  heartbeat_verdict: LivenessVerdict;
  heartbeat_age_seconds: number | null;
}

export interface HostDebugProjection {
  id: string;
  name: string | null;
  organization_id: string | null;
  status: string | null;
  framework: string | null;
  framework_version: string | null;
  last_seen_verdict: LivenessVerdict;
  last_seen_age_seconds: number | null;
  ec2_instance_id: string | null;
  ec2_region: string | null;
  ec2_provisioning_status: string | null;
  claude_auth_mode: string | null;
  claude_auth_status: string | null;
  claude_auth_expires_at: string | null;
}

export interface IntegrationDebugProjection {
  id: string;
  definition_id: string | null;
  status: string | null;
  status_message: string | null;
  auth_type: string | null;
  last_connectivity_check_at: string | null;
  last_connectivity_status: string | null;
  consecutive_connectivity_failures: number | null;
  updated_at: string | null;
}

export interface AlertDebugProjection {
  id: string;
  kind: string | null;
  severity: string | null;
  message: string | null;
  team_id: string | null;
  host_id: string | null;
  agent_id: string | null;
  source: string | null;
  opened_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  acknowledged_at: string | null;
  snoozed_until: string | null;
}

/**
 * ENG-6483: one row of `debug_search_orgs` — a first-class lister for the
 * organizations a staff principal is authorized to read, so org-level triage and
 * access decisions are one call instead of inferring orgs off agent/host rows.
 *
 * `standing_reason` is WHY this org is visible to the caller — `internal`
 * (IL-owned), `fully_managed` (standing customer read), or `granted` (a
 * self-managed org reachable only via an active debug_grant). `has_active_grant`
 * is the orthogonal "do I hold a live grant right now" signal (true even on a
 * standing org with a redundant grant). The counts are diagnostic rollups; pure
 * metadata, same projection-not-raw-rows contract as the rest of the surface.
 */
export type OrgStandingReason = 'internal' | 'fully_managed' | 'granted';

export interface OrgDebugProjection {
  organization_id: string;
  organization_slug: string | null;
  display_name: string | null;
  is_internal: boolean;
  /** The org management mode: `fully_managed` | `self_managed`. */
  management_mode: string | null;
  /** Why this org is readable for the calling principal. */
  standing_reason: OrgStandingReason;
  /** Whether the caller currently holds an active (live, unexpired) debug grant for it. */
  has_active_grant: boolean;
  host_count: number;
  agent_count: number;
  active_agent_count: number;
  /** Open (unclosed) team-scoped alerts for the org. NULL-team infra alerts excluded. */
  open_alert_count: number;
  created_at: string | null;
}

/** Composite returned by `debug_get_agent`: the agent + its host + integrations + recent alerts. */
export interface AgentDebugDetail extends AgentDebugProjection {
  host: HostDebugProjection | null;
  integrations: IntegrationDebugProjection[];
  recent_alerts: AlertDebugProjection[];
}

/**
 * ENG-6518: the result of `debug_get_host` — the host-centric composite, the
 * mirror of `AgentDebugDetail` for host-wide incidents (e.g. an env drift hitting
 * every agent on the box). One read returns the host + every agent bound to it +
 * a rollup of alerts (the host's own infra alerts, including NULL-team ones, PLUS
 * each bound agent's alerts) + a version/restart rollup.
 *
 * The CC/framework version lives on the host projection itself (`framework_version`),
 * so the `rollup` adds only the two host-grain leverage signals from
 * HostVersionProjection: how many agents the host carries and how many times they
 * restarted in the recent window. Pure metadata — same projection-not-raw-rows
 * contract as the rest of the surface.
 */
export interface HostDebugDetail extends HostDebugProjection {
  /** Every agent currently bound to this host (host_agents). */
  agents: AgentDebugProjection[];
  /** Host infra alerts (incl. NULL-team) + each bound agent's alerts, newest first. */
  recent_alerts: AlertDebugProjection[];
  rollup: {
    agent_count: number;
    /** `agent.restart` audit events across the host's agents within the window. */
    restart_count: number;
    restart_window_hours: number;
  };
}

/**
 * ENG-6517: where a host's effective value for ONE feature flag came from.
 * Mirrors the host runtime's own layering (`resolveFlagFromLayers`):
 * env override > heartbeat-materialized (the value the control plane last sent
 * the host) > compiled default. A resolved flag value is `boolean | string`
 * (the registry's `FlagValue`).
 */
export type InspectFlagSource = 'env' | 'heartbeat' | 'default';

export interface InspectFlagsEntry {
  key: string;
  /** The value the HOST is effectively running with (env > heartbeat > default). */
  effective: boolean | string;
  /** Where `effective` came from on the host. */
  source: InspectFlagSource;
  /** The host env-override value (from the heartbeat-reported env_gates), or null. */
  env_value: boolean | string | null;
  /** The env var that overrides this flag on the host, or null if none exists. */
  env_var: string | null;
  /** The value the host last RECEIVED from the control plane (latest snapshot), or null. */
  heartbeat_value: boolean | string | null;
  /** The control plane's CURRENT resolved value for this host scope. */
  central_value: boolean | string;
  /** The compiled registry default. */
  default_value: boolean | string;
  /**
   * True when an env override is masking a DIFFERENT heartbeat-resolved value —
   * the ENG-6478 drift class (an env gate silently overriding the DB-resolved flag).
   */
  env_masks_heartbeat: boolean;
  /** True when the host's last-received value differs from the current central value (host stale). */
  host_stale: boolean;
  sensitive: boolean;
}

/**
 * ENG-6517: the result of `debug_inspect_flags` — an agent/host's EFFECTIVE
 * feature flags WITH source attribution, so the "an env override masked a
 * heartbeat flag" class of drift (ENG-6478) is a one-call lookup instead of
 * WARN-log archaeology. The host-side env/heartbeat values come from the latest
 * `host_config_snapshots` row (ENG-6412); `central_value` is the control plane's
 * current resolution (`getEvaluatedFlags`) for staleness comparison.
 */
export interface InspectFlagsProjection {
  host: { id: string; name: string | null; organization_id: string | null };
  /** The agent the lookup was resolved through, when called with `agent_id`. */
  via_agent_id: string | null;
  /** Latest host config snapshot meta, or null when the host has never reported one. */
  snapshot: {
    captured_at: string;
    config_hash: string;
    flags_schema_version: string | null;
    agt_cli_version: string | null;
  } | null;
  flags: InspectFlagsEntry[];
  /** Keys exhibiting drift (env_masks_heartbeat OR host_stale) — the leverage signal. */
  drift_keys: string[];
  /** The registry schema version the API is running (compare against the snapshot's). */
  flags_schema_version: string;
}

/**
 * ENG-6516: the result of an alert-triage write (debug_ack_alert /
 * debug_snooze_alert / debug_close_alert). Unlike the host-affecting remedial
 * actions (restart, ssm_run, …) these are LOW-RISK control-plane DB mutations on
 * `alerts` state columns — reversible, no customer-host effect — so they are a
 * direct write gated by `ADMIN_DEBUG_WRITE_MODE` + org write-authorization +
 * audit, NOT the Slack-approval machinery (the webapp acks/snoozes the same way).
 *
 * `applied` is false in `shadow` mode (the gate ran, nothing was written). The
 * `alert` projection reflects the post-write state in `enforce` mode, or the
 * current state in `shadow`.
 */
export type AlertTriageAction = 'ack' | 'snooze' | 'close';

export interface AlertTriageResult {
  alert_id: string;
  action: AlertTriageAction;
  /** off ⇒ refused upstream (503); shadow ⇒ no write; enforce ⇒ written. */
  write_mode: 'shadow' | 'enforce';
  /** True only when the row was actually mutated (enforce). */
  applied: boolean;
  alert: {
    id: string;
    organization_id: string | null;
    kind: string;
    severity: string | null;
    acknowledged_at: string | null;
    acknowledged_by: string | null;
    snoozed_until: string | null;
    closed_at: string | null;
    closed_reason: string | null;
  };
}

/**
 * ENG-6431 (#4): one row of `debug_host_versions` — a fleet-wide health
 * snapshot per host. Reuses the host projection (framework_version is the CC
 * version) and adds the two leverage signals an SRE asked for: how many agents
 * the host carries and how many times they restarted in the recent window
 * (a high `restart_count` is the tell for a thrashing host). Pure metadata —
 * same projection-not-raw-rows contract as the rest of the surface.
 */
export interface HostVersionProjection {
  id: string;
  name: string | null;
  organization_id: string | null;
  status: string | null;
  framework: string | null;
  /** The Claude Code / framework version the host last reported. */
  framework_version: string | null;
  last_seen_verdict: LivenessVerdict;
  last_seen_age_seconds: number | null;
  /** Agents currently bound to this host (host_agents). */
  agent_count: number;
  /** `agent.restart` audit events for this host's agents within the window. */
  restart_count: number;
  /** The window `restart_count` was computed over, in hours (default 24). */
  restart_window_hours: number;
}

/**
 * ENG-6431 (#2): the result of `debug_tail_logs` — the trailing lines of ONE
 * allowlisted host log for an authorized agent, fetched live over SSM.
 *
 * Unlike the rest of the surface this DOES carry payload content (`content` is
 * the raw log tail), because a log tail IS the diagnostic — the projection
 * principle ("never raw DB rows") is upheld differently here: the readable set
 * is a fixed allowlist of OPERATIONAL logs (manager.log, pane.log, channel-MCP
 * stderr, manager-state.json) under `~/.augmented/<code_name>/`. Secret-bearing
 * files (`.mcp.json`, `.env.integrations`) are NOT in the allowlist and cannot
 * be reached through this tool. The read is org-walled + audited like every
 * other debug read.
 */
export interface TailLogsProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the tail ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The requested log key (e.g. `manager`, `pane`). */
  log: string;
  /** The resolved relative filename under `~/.augmented/<code_name>/`. */
  log_file: string;
  /** Trailing lines requested (after clamp). */
  lines_requested: number;
  /** False when the file did not exist on the host (content is then ''). */
  log_present: boolean;
  /** The log tail (UTF-8, newest bytes kept when byte-clipped). */
  content: string;
  bytes_returned: number;
  /** True when the tail was clipped to the byte cap (oldest content dropped). */
  truncated: boolean;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  /** Null when no SSM command ran (e.g. no host binding → status `skipped`). */
  ssm_command_id: string | null;
}

/**
 * ENG-6515: the result of `debug_query_logs` — a TIME-WINDOWED read of ONE
 * allowlisted host log, spanning the active file AND its rotated siblings
 * (`manager.log.1`, `manager.log.2.gz`, … — logrotate keeps 14, gzipped). Where
 * `debug_tail_logs` only sees the live tail of the current file (minutes, and
 * gone once it rotates), this resolves the "did it restart at 4pm yesterday?"
 * class of question by reading across rotation boundaries and filtering lines to
 * a `[since, until]` window.
 *
 * Same allowlist + org-wall + audit contract as `debug_tail_logs`: only the
 * fixed set of OPERATIONAL logs is reachable (secret-bearing files are not), the
 * read is org-walled, and every call is audited as a cross-org host access. It
 * carries `content` for the same reason tail does — the log lines ARE the
 * diagnostic.
 *
 * Time filtering keys off the manager's ISO8601 line prefix
 * (`[manager-worker 2026-06-15T14:32:45.123Z] …`); lines without a parseable
 * timestamp inherit the in-window state of the preceding timestamped line (so
 * multi-line entries survive), and a log that carries no timestamps at all
 * (e.g. `pane`, `manager-state`) returns its byte-capped tail unfiltered —
 * `time_filtered` reports which happened.
 */
export interface QueryLogsProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the read ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The requested log key (e.g. `manager`, `pane`). */
  log: string;
  /** The resolved filename (e.g. `manager.log`). */
  log_file: string;
  /** The applied lower bound (normalized ISO8601 UTC, `YYYY-MM-DDTHH:MM:SS`). */
  since: string;
  /** The applied upper bound (normalized ISO8601 UTC, `YYYY-MM-DDTHH:MM:SS`). */
  until: string;
  /** Max lines returned (after clamp; newest kept when clipped). */
  lines_requested: number;
  /** Whether reliable ISO8601 line-timestamp filtering applies to this log. */
  time_filtered: boolean;
  /** False when neither the active file nor any rotated sibling existed. */
  log_present: boolean;
  /** The matched log content (UTF-8, newest bytes kept when byte-clipped). */
  content: string;
  bytes_returned: number;
  /** True when the result was clipped to the byte/line cap (oldest content dropped). */
  truncated: boolean;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  /** Null when no SSM command ran (e.g. no host binding → status `skipped`). */
  ssm_command_id: string | null;
}

/**
 * ENG-6431 (#1, PR B): the result of `debug_probe_integration` — a LIVE
 * connectivity verdict for ONE installed integration, produced by SSM-invoking
 * the host primitive `agt integration probe <code_name> <slug> --json`
 * (ENG-6441) on the agent's current host. This is NOT the cached
 * `last_connectivity_status` the central `POST /integrations/:id/test` echoes;
 * the probe runs fresh on the host (the only place the agent's wired
 * `.mcp.json` + `.env.integrations` exist), so the verdict is ground truth.
 *
 * `verdict` is the host probe's `ConnectivityStatus` (ok | degraded |
 * transient_error | down) or `not_probeable` when no probe is wired for that
 * integration kind — PLUS the central-derived non-verdicts below when no clean
 * host verdict was produced:
 *  - `unreachable`      — the agent has no current host binding / no instance id.
 *  - `not_installed`    — the host reported the integration isn't installed.
 *  - `host_cli_too_old` — the host's agt-cli predates `agt integration probe`.
 *  - `probe_error`      — SSM ran but the verdict couldn't be obtained (timeout,
 *                         non-zero exit, unparseable output).
 * Org-walled + audited like every other host-reaching debug read.
 */
export type ProbeIntegrationVerdict =
  | 'ok'
  | 'degraded'
  | 'transient_error'
  | 'down'
  | 'not_probeable'
  | 'not_installed'
  | 'host_cli_too_old'
  | 'probe_error'
  | 'unreachable';

/** The host probe statuses that come straight back from `agt integration probe --json`. */
export const HOST_PROBE_STATUSES: ReadonlySet<string> = new Set([
  'ok',
  'degraded',
  'transient_error',
  'down',
  'not_probeable',
]);

export interface ProbeIntegrationProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The integration slug (definition code_name) that was probed. */
  slug: string;
  /** The host the probe ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** The live connectivity verdict, or a central-derived non-verdict (see above). */
  verdict: ProbeIntegrationVerdict;
  /** Human-readable detail from the host probe, or why no verdict was produced. */
  message: string | null;
  /** ISO timestamp the host stamped the probe (null when no host verdict). */
  probed_at: string | null;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  ssm_command_id: string | null;
}

/** Raw host-command result the verdict resolver classifies (decoupled from SSM types). */
export interface RawHostProbeResult {
  stdout: string;
  stderr: string;
  responseCode: number | null;
  timedOut: boolean;
}

export interface ProbeVerdictResolution {
  verdict: ProbeIntegrationVerdict;
  message: string | null;
  probed_at: string | null;
}

/** First non-empty line of a blob, trimmed and length-capped — for a tidy message. */
function firstLine(s: string, max = 300): string {
  const line = (s.split('\n').find((l) => l.trim().length > 0) ?? '').trim();
  return line.length > max ? line.slice(0, max) : line;
}

/**
 * Find the host primitive's `--json` object in stdout. JSON mode silences the
 * spinner, but be defensive: try the whole trimmed blob first, then fall back to
 * the LAST line that parses to an object (so a stray leading line can't defeat it).
 */
function parseProbeJson(stdout: string): Record<string, unknown> | null {
  const tryParse = (s: string): Record<string, unknown> | null => {
    const t = s.trim();
    if (!t.startsWith('{') || !t.endsWith('}')) return null;
    try {
      const v = JSON.parse(t) as unknown;
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };
  const whole = tryParse(stdout);
  if (whole) return whole;
  const lines = stdout.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const parsed = tryParse(lines[i]!);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * Classify a raw host-probe command result into a verdict. Pure (no I/O) so it
 * is unit-testable without SSM. A clean host verdict (parsed `--json` with a
 * known status) passes straight through; everything else is mapped to a
 * central-derived non-verdict (old CLI, not-installed, timeout, parse/exit error)
 * so the tool degrades gracefully rather than crashing — AC: "graceful
 * degradation on an old-CLI host".
 */
export function resolveProbeVerdict(r: RawHostProbeResult): ProbeVerdictResolution {
  const parsed = parseProbeJson(r.stdout);
  if (parsed && typeof parsed['status'] === 'string' && HOST_PROBE_STATUSES.has(parsed['status'])) {
    return {
      verdict: parsed['status'] as ProbeIntegrationVerdict,
      message: typeof parsed['message'] === 'string' ? parsed['message'] : null,
      probed_at: typeof parsed['probed_at'] === 'string' ? parsed['probed_at'] : null,
    };
  }

  if (r.timedOut) {
    return {
      verdict: 'probe_error',
      message: 'the probe did not return a verdict before the poll ceiling',
      probed_at: null,
    };
  }

  const stderr = r.stderr ?? '';
  // Commander prints `error: unknown command 'probe'` (or `unknown option`) on a
  // CLI that predates `agt integration probe`. That is the old-CLI tell.
  if (/unknown command|unknown option|did you mean|see --help/i.test(stderr)) {
    return {
      verdict: 'host_cli_too_old',
      message:
        "the host's agt-cli predates `agt integration probe` — update the host CLI to a build that carries it (ENG-6441)",
      probed_at: null,
    };
  }
  // The host primitive's not-installed error path (stderr, no JSON).
  if (/is not installed on/i.test(stderr)) {
    return { verdict: 'not_installed', message: firstLine(stderr), probed_at: null };
  }

  const detail =
    firstLine(stderr) ||
    firstLine(r.stdout) ||
    `host probe failed (exit ${r.responseCode ?? 'n/a'}) with no output`;
  return { verdict: 'probe_error', message: detail, probed_at: null };
}

/**
 * ENG-6443 (#6, Tier C): dead-lettered / quarantined inbound for ONE agent.
 *
 * An inbound message (Slack/Telegram) is "dead-lettered" when it is parked on the
 * host instead of being delivered — moved to a `*-pending-inbound-stale/` dir on a
 * wedge-respawn (the agent couldn't receive it), or moved aside into a
 * `pending-inbound-cleared-<stamp>/` dir by the `clear_pending_inbound` remedial
 * action. Both are recoverable: the marker file still holds the original message.
 * `debug_inspect_dead_letters` LISTS them; `debug_replay_dead_letter` re-injects a
 * selected one back into the agent's live `*-pending-inbound/` dir.
 *
 * SAFETY — marker filenames are ATTACKER-CONTROLLED (written by the agent's own
 * channel MCP from untrusted channel/chat/message ids). They are enumerated host
 * side via `readdir` and are NEVER interpolated into a shell command or used to
 * construct a path from caller input; the projection below carries only routing
 * metadata (which conversation, when, why) — NEVER the message `payload` body, so
 * inspecting a dead letter cannot exfiltrate customer message content into our
 * audit log.
 */
export type DeadLetterChannel = 'slack' | 'telegram';

export interface DeadLetterMarker {
  /**
   * 0-based position in the host's canonical enumeration — display/order only.
   * NOT the replay key: replay matches by (store, channel, marker_name), which is
   * TOCTOU-immune (the index can shift if markers arrive/clear between calls).
   */
  index: number;
  /**
   * Which dead-letter store the marker lives in: `stale` (a wedge-respawn
   * dead-letter) or `cleared:<dirname>` (a `clear_pending_inbound` move-aside).
   */
  store: string;
  channel: DeadLetterChannel;
  /** The on-disk marker filename. Attacker-controlled; the replay key (with store+channel). */
  marker_name: string;
  /** ISO timestamp the channel MCP stamped when it parked the inbound (null if unparseable). */
  received_at: string | null;
  /** True when the marker was flagged undeliverable (agent couldn't receive) before dead-lettering. */
  undeliverable: boolean;
  /** True when the inbound was discretionary/auto-followed (a lower-confidence replay candidate). */
  discretionary: boolean;
  /** Durable-replay attempt count (null when the marker carries no replay payload). */
  replay_count: number | null;
  /** Conversation routing id (Slack channel id / Telegram chat id) — NOT message content. */
  conversation: string | null;
  /** Message reference (Slack message_ts / Telegram message_id) — NOT message content. */
  message_ref: string | null;
  /** True when the marker JSON could not be parsed host-side (listed by name only). */
  parse_error: boolean;
}

export interface DeadLettersProjection {
  agent_id: string;
  code_name: string;
  organization_id: string | null;
  /** The host the inspect ran against (null when the agent has no current binding). */
  host: { id: string; name: string | null } | null;
  /** Dead-letter markers in the host's canonical order (slack-stale, telegram-stale, then cleared dirs). */
  markers: DeadLetterMarker[];
  /** Total markers found (== markers.length). */
  total: number;
  /** SSM invocation status: Success | Failed | TimedOut | Cancelled | skipped. */
  ssm_status: string;
  ssm_command_id: string | null;
}

/** Outcome of parsing the host inspect script's JSONL stdout. */
export interface DeadLetterInspectParse {
  markers: DeadLetterMarker[];
  /** Set when the host emitted a `dead_letter_error` sentinel (node missing / bad code_name). */
  hostError: string | null;
}

function coerceDeadLetterChannel(v: unknown): DeadLetterChannel | null {
  return v === 'slack' || v === 'telegram' ? v : null;
}

// ───────────────────── stuck restart requests (ENG-6444) ─────────────────────

/**
 * ENG-6444: how long a `host_agents.restart_requested_at` must sit unacked before
 * it counts as STUCK. Mirrors `STUCK_THRESHOLD_SECONDS` in the API's
 * agent-restart-monitor cron (the writer of the `agent_restart_stuck` alert) — the
 * manager should ack a restart within 1–2 ticks, well inside 15 min. Kept here so
 * the `debug_inspect_restart_requests` read flags `is_stuck` on the same boundary
 * the alert opens on. (The cron keeps its own copy; this is a deliberate mirror,
 * not an import, to avoid the read depending on a cron-internal constant.)
 */
export const STUCK_RESTART_THRESHOLD_SECONDS = 15 * 60;

/**
 * ENG-6444: one pending restart request for the `debug_inspect_restart_requests`
 * read. A "restart request" is a non-null `host_agents.restart_requested_at` the
 * manager hasn't acked yet; `is_stuck` is true once its age crosses
 * `STUCK_RESTART_THRESHOLD_SECONDS` (the point the agent-restart-monitor cron opens
 * an `agent_restart_stuck` alert). The `incident` / `alert` fields surface the
 * open ledger row + paged alert when the cron has already escalated it. Pure
 * routing/metadata — no message content, same projection-not-raw-rows contract as
 * the rest of the surface. `request_clear_restart_request` cancels one.
 */
export interface StuckRestartRequestProjection {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  organization_id: string | null;
  /** The host whose binding carries the unacked restart signal. */
  host: { id: string; name: string | null } | null;
  /** When the restart was requested (the unacked `host_agents.restart_requested_at`). */
  restart_requested_at: string;
  /** Age of the request, in seconds. */
  stuck_seconds: number;
  /** True once `stuck_seconds` crosses the threshold the alert opens on. */
  is_stuck: boolean;
  /** The open `agent_restart_incidents` ledger row, if the cron has escalated it. */
  incident: {
    id: string;
    reason: string | null;
    opened_at: string | null;
    acknowledged_at: string | null;
  } | null;
  /** The open `agent_restart_stuck` alert, if the cron has paged it. */
  alert: {
    id: string;
    severity: string | null;
    message: string | null;
    opened_at: string | null;
  } | null;
}

/**
 * Parse the host inspect script's stdout (one JSON object per line). Pure (no I/O)
 * so it is unit-testable without SSM. Renumbers `index` by array position so the
 * returned order is contiguous and authoritative regardless of host output. Lines
 * that aren't a valid marker object are skipped; a `dead_letter_error` sentinel
 * line surfaces as `hostError`.
 */
export function parseDeadLetterInspectOutput(stdout: string): DeadLetterInspectParse {
  const markers: DeadLetterMarker[] = [];
  let hostError: string | null = null;
  for (const rawLine of (stdout ?? '').split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('{') || !line.endsWith('}')) continue;
    let obj: Record<string, unknown>;
    try {
      const v = JSON.parse(line) as unknown;
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
      obj = v as Record<string, unknown>;
    } catch {
      continue;
    }
    if (typeof obj['dead_letter_error'] === 'string') {
      hostError = obj['dead_letter_error'];
      continue;
    }
    const channel = coerceDeadLetterChannel(obj['channel']);
    const store = obj['store'];
    const markerName = obj['marker_name'];
    if (!channel || typeof store !== 'string' || typeof markerName !== 'string') continue;
    markers.push({
      index: markers.length,
      store,
      channel,
      marker_name: markerName,
      received_at: typeof obj['received_at'] === 'string' ? obj['received_at'] : null,
      undeliverable: obj['undeliverable'] === true,
      discretionary: obj['discretionary'] === true,
      replay_count: typeof obj['replay_count'] === 'number' ? obj['replay_count'] : null,
      conversation: typeof obj['conversation'] === 'string' ? obj['conversation'] : null,
      message_ref: typeof obj['message_ref'] === 'string' ? obj['message_ref'] : null,
      parse_error: obj['parse_error'] === true,
    });
  }
  return { markers, hostError };
}

/** Outcome of a replay (re-inject) — parsed from the host replay script's stdout. */
export interface DeadLetterReplayResolution {
  replayed: boolean;
  /** When not replayed: why (`not_found` | `move_failed` | `node_not_found` | `bad_code_name` | `no_output`). */
  reason: string | null;
  channel: string | null;
  store: string | null;
  marker_name: string | null;
  /** The live dir the marker was moved into (e.g. `slack-pending-inbound`). */
  moved_to: string | null;
}

/**
 * Parse the host replay script's stdout. Pure (no I/O). The replay script emits a
 * single JSON object: a success (`replayed:true` + what moved) or a structured
 * reason (`not_found` when the marker is gone, `move_failed`, or a `dead_letter_error`
 * sentinel). Anything unparseable degrades to `no_output`.
 */
export function parseDeadLetterReplayOutput(stdout: string): DeadLetterReplayResolution {
  const fallback: DeadLetterReplayResolution = {
    replayed: false,
    reason: 'no_output',
    channel: null,
    store: null,
    marker_name: null,
    moved_to: null,
  };
  const lines = (stdout ?? '').split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]!.trim();
    if (!line.startsWith('{') || !line.endsWith('}')) continue;
    let obj: Record<string, unknown>;
    try {
      const v = JSON.parse(line) as unknown;
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
      obj = v as Record<string, unknown>;
    } catch {
      continue;
    }
    if (typeof obj['dead_letter_error'] === 'string') {
      return { ...fallback, reason: obj['dead_letter_error'] };
    }
    if (obj['replayed'] === true || obj['replayed'] === false) {
      return {
        replayed: obj['replayed'] === true,
        reason: typeof obj['reason'] === 'string' ? obj['reason'] : null,
        channel: typeof obj['channel'] === 'string' ? obj['channel'] : null,
        store: typeof obj['store'] === 'string' ? obj['store'] : null,
        marker_name: typeof obj['marker_name'] === 'string' ? obj['marker_name'] : null,
        moved_to: typeof obj['moved_to'] === 'string' ? obj['moved_to'] : null,
      };
    }
  }
  return fallback;
}
